if (!window.LimoShopGridSections) {
  window.LimoShopGridSections = {};
}

class LimoShopGridMobileSlider {
  constructor(sectionEl) {
    this.sectionEl = sectionEl;
    this.gridEl = sectionEl.querySelector('.limo-shop-grid__grid');
    this.paginationEl = sectionEl.querySelector('[data-limo-shop-grid-pagination]');

    if (!this.gridEl || !this.paginationEl) return;

    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    this.itemsPerSlide = Math.max(
      1,
      parseInt(sectionEl.getAttribute('data-mobile-items-per-slide') || '2', 10) || 2
    );
    this.sliderEnabled = sectionEl.getAttribute('data-mobile-slider-enabled') === 'true';
    this.originalItems = Array.from(this.gridEl.querySelectorAll('.limo-shop-grid__item'));
    this.isWrappedIntoSlides = false;
    this.pageCount = 0;
    this.activePage = 0;
    this.snapPoints = [];

    this.isPointerDown = false;
    this.dragMoved = false;
    this.isDragScrolling = false;
    this.suppressClick = false;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.startScrollLeft = 0;

    this.handleResize = this.handleResize.bind(this);
    this.handleGridScroll = this.handleGridScroll.bind(this);
    this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleGridClickCapture = this.handleGridClickCapture.bind(this);
    this.handleNativeDragStart = this.handleNativeDragStart.bind(this);

    this.init();
  }

  init() {
    this.gridEl.addEventListener('scroll', this.handleGridScroll, { passive: true });
    this.gridEl.addEventListener('mousedown', this.handlePointerDown);
    this.gridEl.addEventListener('click', this.handleGridClickCapture, true);
    this.gridEl.addEventListener('dragstart', this.handleNativeDragStart);
    window.addEventListener('mousemove', this.handlePointerMove);
    window.addEventListener('mouseup', this.handlePointerUp);
    window.addEventListener('resize', this.handleResize);

    if (typeof this.mobileMediaQuery.addEventListener === 'function') {
      this.mobileMediaQuery.addEventListener('change', this.handleMediaQueryChange);
    } else if (typeof this.mobileMediaQuery.addListener === 'function') {
      this.mobileMediaQuery.addListener(this.handleMediaQueryChange);
    }

    this.rebuild();
  }

  handleMediaQueryChange() {
    this.rebuild();
  }

  handleResize() {
    this.rebuild(false);
  }

  handleGridScroll() {
    if (!this.isSliderActive()) return;
    this.setActivePageFromScroll();
  }

  handlePointerDown(event) {
    if (!this.isSliderActive()) return;
    if (event.button !== 0) return;
    this.isPointerDown = true;
    this.dragMoved = false;
    this.isDragScrolling = false;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.startScrollLeft = this.gridEl.scrollLeft;
  }

  handlePointerMove(event) {
    if (!this.isPointerDown) return;
    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (!this.isDragScrolling) {
      const isHorizontalIntent = absX > 10 && absX > absY + 2;
      if (!isHorizontalIntent) return;
      this.isDragScrolling = true;
      this.gridEl.classList.add('is-dragging');
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    this.dragMoved = true;
    this.gridEl.scrollLeft = this.startScrollLeft - deltaX;
  }

  handlePointerUp() {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.gridEl.classList.remove('is-dragging');
    if (this.isSliderActive() && this.isDragScrolling && this.dragMoved) {
      this.suppressClick = true;
    }
    this.isDragScrolling = false;
  }

  handleGridClickCapture(event) {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    this.suppressClick = false;
  }

  handleNativeDragStart(event) {
    if (!this.isSliderActive() || !this.isPointerDown) return;
    event.preventDefault();
  }

  isSliderActive() {
    return this.sliderEnabled && this.mobileMediaQuery.matches;
  }

  readSettingsFromDataset() {
    this.itemsPerSlide = Math.max(
      1,
      parseInt(this.sectionEl.getAttribute('data-mobile-items-per-slide') || '2', 10) || 2
    );
    this.sliderEnabled = this.sectionEl.getAttribute('data-mobile-slider-enabled') === 'true';
  }

  getSlides() {
    return Array.from(this.gridEl.querySelectorAll('.limo-shop-grid__slide'));
  }

  unwrapSlides() {
    if (!this.isWrappedIntoSlides) return;
    this.getSlides().forEach((slide) => slide.remove());
    this.originalItems.forEach((item) => this.gridEl.appendChild(item));
    this.isWrappedIntoSlides = false;
  }

  wrapItemsIntoSlides() {
    this.unwrapSlides();
    if (this.originalItems.length === 0) return;

    for (let start = 0; start < this.originalItems.length; start += this.itemsPerSlide) {
      const slide = document.createElement('div');
      slide.className = 'limo-shop-grid__slide';
      for (let idx = start; idx < start + this.itemsPerSlide && idx < this.originalItems.length; idx += 1) {
        slide.appendChild(this.originalItems[idx]);
      }
      this.gridEl.appendChild(slide);
    }

    this.isWrappedIntoSlides = true;
  }

  computeSnapPoints() {
    const items = Array.from(this.gridEl.querySelectorAll('.limo-shop-grid__item'));
    const maxScrollable = Math.max(0, this.gridEl.scrollWidth - this.gridEl.clientWidth);
    const unique = [];

    items.forEach((item) => {
      const point = Math.round(item.offsetLeft);
      if (point > maxScrollable + 1) return;
      const exists = unique.some((value) => Math.abs(value - point) <= 1);
      if (!exists) unique.push(point);
    });

    if (!unique.some((value) => Math.abs(value - maxScrollable) <= 1)) {
      unique.push(maxScrollable);
    }

    unique.sort((a, b) => a - b);
    this.snapPoints = unique;
  }

  rebuild(resetScroll = true) {
    this.readSettingsFromDataset();

    if (!this.isSliderActive()) {
      this.isPointerDown = false;
      this.dragMoved = false;
      this.isDragScrolling = false;
      this.suppressClick = false;
      this.gridEl.classList.remove('is-dragging');
      this.unwrapSlides();
      this.paginationEl.innerHTML = '';
      return;
    }

    this.wrapItemsIntoSlides();
    this.computeSnapPoints();
    this.pageCount = this.snapPoints.length;
    this.activePage = 0;

    if (resetScroll) {
      this.gridEl.scrollLeft = 0;
    }

    this.renderPagination();
    this.setActivePageFromScroll();
  }

  renderPagination() {
    this.paginationEl.innerHTML = '';
    if (this.pageCount <= 1) return;

    for (let pageIndex = 0; pageIndex < this.pageCount; pageIndex += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'limo-shop-grid__pagination-dot';
      dot.setAttribute('aria-label', `Go to slide ${pageIndex + 1}`);
      dot.addEventListener('click', () => this.scrollToPage(pageIndex));
      this.paginationEl.appendChild(dot);
    }
  }

  scrollToPage(pageIndex) {
    if (!this.isSliderActive()) return;
    const targetLeft = this.snapPoints[pageIndex];
    if (typeof targetLeft !== 'number') return;

    this.gridEl.scrollTo({
      left: targetLeft,
      behavior: 'smooth'
    });
  }

  setActivePageFromScroll() {
    if (this.pageCount <= 1 || this.snapPoints.length === 0) return;

    let nearestIndex = 0;
    let nearestDistance = Infinity;
    const currentScroll = this.gridEl.scrollLeft;

    for (let index = 0; index < this.snapPoints.length; index += 1) {
      const distance = Math.abs(this.snapPoints[index] - currentScroll);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    }

    this.activePage = Math.min(this.pageCount - 1, nearestIndex);
    this.updateActiveIndicator();
  }

  updateActiveIndicator() {
    const dots = Array.from(this.paginationEl.querySelectorAll('.limo-shop-grid__pagination-dot'));
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activePage);
    });
  }

  destroy() {
    this.gridEl.removeEventListener('scroll', this.handleGridScroll);
    this.gridEl.removeEventListener('mousedown', this.handlePointerDown);
    this.gridEl.removeEventListener('click', this.handleGridClickCapture, true);
    this.gridEl.removeEventListener('dragstart', this.handleNativeDragStart);
    window.removeEventListener('mousemove', this.handlePointerMove);
    window.removeEventListener('mouseup', this.handlePointerUp);
    window.removeEventListener('resize', this.handleResize);
    if (typeof this.mobileMediaQuery.removeEventListener === 'function') {
      this.mobileMediaQuery.removeEventListener('change', this.handleMediaQueryChange);
    } else if (typeof this.mobileMediaQuery.removeListener === 'function') {
      this.mobileMediaQuery.removeListener(this.handleMediaQueryChange);
    }
    this.unwrapSlides();
    this.paginationEl.innerHTML = '';
  }
}

function initLimoShopGridSection(sectionEl) {
  const sectionId = sectionEl.dataset.sectionId || sectionEl.id;
  if (!sectionId) return;
  if (window.LimoShopGridSections[sectionId]) return;
  const instance = new LimoShopGridMobileSlider(sectionEl);
  window.LimoShopGridSections[sectionId] = instance;
}

function bootLimoShopGridSections() {
  document.querySelectorAll('[data-limo-shop-grid-section]').forEach((sectionEl) => {
    initLimoShopGridSection(sectionEl);
  });
}

bootLimoShopGridSections();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!target) return;
  const sectionEl = target.matches && target.matches('[data-limo-shop-grid-section]')
    ? target
    : target.querySelector
      ? target.querySelector('[data-limo-shop-grid-section]')
      : null;
  if (!sectionEl) return;
  initLimoShopGridSection(sectionEl);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (!target) return;
  const sectionEl = target.matches && target.matches('[data-limo-shop-grid-section]')
    ? target
    : target.querySelector
      ? target.querySelector('[data-limo-shop-grid-section]')
      : null;
  if (!sectionEl) return;
  const sectionId = sectionEl.dataset.sectionId || sectionEl.id;
  if (!sectionId) return;
  const instance = window.LimoShopGridSections[sectionId];
  if (instance && typeof instance.destroy === 'function') {
    instance.destroy();
  }
  delete window.LimoShopGridSections[sectionId];
}
);
